import { DraftJSFormat, InlineStyleRange, LexicalFormat, LexicalNode } from "components/ContentEditor/converter/types";

// Helper to create a plain text node
const createPlainTextNode = (text: string): LexicalNode => ({
	detail: 0,
	format: 0,
	mode: "normal",
	style: "",
	text,
	type: "text",
	version: 1,
});

// Helper to get format flags for a character position from inline styles
const getFormatFlagsFromStyles = (styles: InlineStyleRange[], charPos: number): number => {
	let format = 0;
	for (const style of styles) {
		if (charPos >= style.offset && charPos < style.offset + style.length) {
			if (style.style === "BOLD") format |= 1;
			if (style.style === "ITALIC") format |= 2;
		}
	}
	return format;
};

// Helper to create text nodes with proper formatting from draft-js inline styles
const createFormattedTextNodes = (text: string, styles: InlineStyleRange[], textOffset: number = 0): LexicalNode[] => {
	if (!styles || styles.length === 0) {
		return [createPlainTextNode(text)];
	}

	// Filter styles that apply to this text segment
	const applicableStyles = styles.filter((style) => style.offset < textOffset + text.length && style.offset + style.length > textOffset);

	if (applicableStyles.length === 0) {
		return [createPlainTextNode(text)];
	}

	const nodes: LexicalNode[] = [];
	let lastPos = 0;

	// Create text nodes by breaking at style boundaries
	for (let i = 0; i < text.length; i++) {
		const charPos = textOffset + i;
		const format = getFormatFlagsFromStyles(applicableStyles, charPos);
		const nextCharPos = charPos + 1;
		const nextFormat = getFormatFlagsFromStyles(applicableStyles, nextCharPos);

		// If format changes or we're at the end, push current text node
		if (nextFormat !== format || i === text.length - 1) {
			nodes.push({
				detail: 0,
				format,
				mode: "normal",
				style: "",
				text: text.substring(lastPos, i + 1),
				type: "text",
				version: 1,
			});
			lastPos = i + 1;
		}
	}

	return nodes;
};

/**
 * Convert draft-js format to Lexical format
 */
export function draftJSToLexical(draftJSContent: string | DraftJSFormat): LexicalFormat {
	try {
		const parsed: DraftJSFormat = typeof draftJSContent === "string" ? JSON.parse(draftJSContent) : draftJSContent;
		const blocks = Array.isArray(parsed) ? parsed : parsed.blocks;
		const entityMap = parsed.entityMap || {};

		const children: LexicalNode[] = [];
		let currentList: LexicalNode | null = null;
		let currentListType: "bullet" | "number" | null = null;

		for (const block of blocks) {
			// Determine node type
			let type = "paragraph";
			let level: "h1" | "h2" | "h3" | undefined = undefined;
			let isListItem = false;
			let listType: "bullet" | "number" | undefined = undefined;

			if (block.type === "header-one") {
				type = "heading";
				level = "h1";
			} else if (block.type === "header-two") {
				type = "heading";
				level = "h2";
			} else if (block.type === "header-three") {
				type = "heading";
				level = "h3";
			} else if (block.type === "unordered-list-item") {
				type = "listitem";
				isListItem = true;
				listType = "bullet";
			} else if (block.type === "ordered-list-item") {
				type = "listitem";
				isListItem = true;
				listType = "number";
			} else if (block.type === "atomic") {
				// Handle atomic blocks (images, videos, etc.)
				if (block.entityRanges && block.entityRanges.length > 0) {
					const entity = block.entityRanges[0];
					const entityData = entityMap[entity.key];
					if (entityData && entityData.type === "IMAGE") {
						children.push({
							type: "image",
							version: 1,
							src: entityData.data?.src || "",
							altText: entityData.data?.alt || "",
							width: entityData.data?.width || "auto",
							height: entityData.data?.height || "auto",
						} as LexicalNode);
					}
				}
				continue;
			}

			// Build text with links
			const textChildren: LexicalNode[] = [];
			if (block.text) {
				// Check for entity ranges (links)
				if (block.entityRanges && block.entityRanges.length > 0) {
					let currentPos = 0;

					// Sort entity ranges by offset
					const sortedRanges = [...block.entityRanges].sort((a, b) => a.offset - b.offset);

					for (const entity of sortedRanges) {
						// Add text before the entity with formatting
						if (currentPos < entity.offset) {
							const beforeText = block.text.substring(currentPos, entity.offset);
							const formattedNodes = createFormattedTextNodes(beforeText, block.inlineStyleRanges || [], currentPos);
							textChildren.push(...formattedNodes);
						}

						// Add linked text with formatting
						const entityData = entityMap[entity.key];
						if (entityData && entityData.type === "LINK") {
							const linkUrl = entityData.data?.url || "#";
							const linkText = block.text.substring(entity.offset, entity.offset + entity.length);
							const linkFormattedNodes = createFormattedTextNodes(linkText, block.inlineStyleRanges || [], entity.offset);

							textChildren.push({
								children: linkFormattedNodes.length > 0 ? linkFormattedNodes : [createPlainTextNode(linkText)],
								direction: null,
								format: "",
								indent: 0,
								type: "link",
								version: 1,
								url: linkUrl,
								rel: null,
								target: entityData.data?.targetOption || null,
							});
						} else if (entityData && entityData.type === "IMAGE") {
							textChildren.push({
								altText: entityData.data?.alt || "",
								direction: null,
								format: "",
								indent: 0,
								type: "image",
								version: 1,
								src: entityData.data?.src || "",
								width: (entityData.data?.width as number) || undefined,
								height: (entityData.data?.height as number) || undefined,
							});
						}

						currentPos = entity.offset + entity.length;
					}

					// Add remaining text with formatting
					if (currentPos < block.text.length) {
						const remainingText = block.text.substring(currentPos);
						const formattedNodes = createFormattedTextNodes(remainingText, block.inlineStyleRanges || [], currentPos);
						textChildren.push(...formattedNodes);
					}
				} else {
					// No links, just text
					const formattedNodes = createFormattedTextNodes(block.text, block.inlineStyleRanges || [], 0);
					textChildren.push(...formattedNodes);
				}
			}

			if (isListItem) {
				// Check if we need to start a new list or continue existing one
				if (!currentList || currentListType !== listType) {
					// Save the previous list if it exists
					if (currentList) {
						children.push(currentList);
					}

					// Start a new list
					currentList = {
						children: [],
						direction: null,
						format: "",
						indent: 0,
						type: "list",
						version: 1,
						listType,
						start: 1,
					};
					currentListType = listType ?? null;
				}

				// Add item to current list
				currentList.children?.push({
					children: [
						{
							children:
								textChildren.length > 0
									? textChildren
									: [
											{
												detail: 0,
												format: 0,
												mode: "normal",
												style: "",
												text: "",
												type: "text",
												version: 1,
											},
										],
							direction: null,
							format: "",
							indent: 0,
							type: "paragraph",
							version: 1,
							textFormat: 0,
							textStyle: "",
						},
					],
					direction: null,
					format: "",
					indent: 0,
					type: "listitem",
					version: 1,
					value: currentList.children.length + 1,
				});
			} else {
				// Save the previous list if it exists
				if (currentList) {
					children.push(currentList);
					currentList = null;
					currentListType = null;
				}

				const nodeData: LexicalNode = {
					children: textChildren.length > 0 ? textChildren : [],
					direction: null,
					format: "",
					indent: 0,
					type,
					version: 1,
					textFormat: 0,
					textStyle: "",
				};

				// Add heading level for heading nodes
				if (level) {
					nodeData.level = level;
				}

				children.push(nodeData);
			}
		}

		// Don't forget to add the last list if it exists
		if (currentList) {
			children.push(currentList);
		}

		return {
			root: {
				children,
				direction: null,
				format: "",
				indent: 0,
				type: "root",
				version: 1,
			},
		};
	} catch (e) {
		console.error("Error converting draft-js to Lexical:", e);
		return {
			root: {
				children: [],
				direction: null,
				format: "",
				indent: 0,
				type: "root",
				version: 1,
			},
		};
	}
}
